import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Input, Button, message } from "antd";
import { useDispatch } from "react-redux";
import { sendEmailOtp, validateEmailOtp } from "../../redux/AuthController";
import { useNavigate } from "react-router-dom";
import { Player } from "@lottiefiles/react-lottie-player";

const EmailVerificationModal = ({ visible, onCancel, onVerify, email, session: sessionProp }) => {
  const [otpFields, setOtpFields] = useState(["", "", "", "", ""]);
  const [timer, setTimer] = useState(60);
  const [new_email, setEmail] = useState(email);
  const [isResending, setIsResending] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  useEffect(() => {
    setEmail(email);
  }, [email]);

 
  const resolveSession = () => {
    if (sessionProp) return sessionProp;

    const userInfoString = localStorage.getItem("userInfo");
    if (!userInfoString) {
      message.error("User information not found. Please try again.");
      return null;
    }

    let userInfo;
    try {
      userInfo = JSON.parse(userInfoString);
    } catch (error) {
      message.error("Invalid user information. Please try again.");
      return null;
    }

    const session = userInfo?.session;
    if (!session) {
      message.error("Session not found. Please try again.");
      return null;
    }

    return session;
  };

  const handleResendOtp = useCallback(async () => {
    // Guard against double-firing (e.g. a fast double-click on "Resend Code").
    if (isResending) return;

    const session = resolveSession();
    if (!session) return;

    setIsResending(true);
    try {
      await dispatch(sendEmailOtp({ email: new_email, session })).unwrap();
      setTimer(60);
      message.success(`Verification code sent to ${new_email}`);
    } catch (error) {
      message.error("Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, new_email, sessionProp, isResending]);

  useEffect(() => {
    if (!visible) return;
    const intervalId = setInterval(() => {
      setTimer((t) => (t > 0 ? t - 1 : 0));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [visible]);

  useEffect(() => {
    if (visible) {
      setTimer(60);
    }
  }, [visible]);

  const handleInputChange = (index, value) => {
    // Allow pasting of multiple digits
    if (value.length > 1) {
      // Distribute the pasted digits across the fields
      const updatedOtpFields = [...otpFields];
      let pasteIndex = index;
      for (let i = 0; i < value.length; i++) {
        if (pasteIndex >= otpFields.length) break;
        if (/^\d$/.test(value[i])) {
          updatedOtpFields[pasteIndex] = value[i];
          pasteIndex++;
        }
      }
      setOtpFields(updatedOtpFields);
      // Focus on the next field if necessary
      if (pasteIndex < otpFields.length) {
        inputRefs.current[pasteIndex].focus();
      }
    } else {
      // Handle single digit input as before
      if (/^\d$/.test(value) || value === "") {
        const updatedOtpFields = [...otpFields];
        updatedOtpFields[index] = value;
        setOtpFields(updatedOtpFields);

        if (value !== "" && index < otpFields.length - 1) {
          inputRefs.current[index + 1].focus();
        }
      }
    }
  };


  const handleVerify = async () => {
    const otp = otpFields.join("");

    if (!new_email || !new_email.trim()) {
      message.error("Email is required for verification. Please try again.");
      return;
    }

    const session = resolveSession();
    if (!session) return;

    try {
      const result = await dispatch(
        validateEmailOtp({ email: new_email, code: otp, session }),
      ).unwrap();
      if (result.statusCode === "200" && result?.status) {
        message.success("Verification successful, Please login!");
        onVerify?.(result);
        navigate("/");
      } else {
        message.error("Verification Unsuccessful!");
      }
    } catch (error) {
      message.error(error);
    }
    setOtpFields(["", "", "", "", ""]);

  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      width={700}
      centered
      title="PLEASE VERIFY YOUR EMAIL ADDRESS"
      footer={[
        <div
          key="buttons"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: 30,
          }}
        >
          {timer > 0 ? (
            <p style={{ marginBottom: 10 }}>{`Resend in ${timer}s`}</p>
          ) : (
            <p
              style={{
                marginBottom: 10,
                color: "#01ADF0",
                fontWeight: "bold",
                textDecoration: "underline",
                cursor: isResending ? "default" : "pointer",
              }}
              onClick={isResending ? undefined : handleResendOtp}
            >
              {isResending ? "Sending..." : "Resend Code"}
            </p>
          )}
          <Button
            key="verify"
            type="primary"
            onClick={handleVerify}
            style={{
              width: "100%",
              height: "42.82px",
              borderRadius: 20,
              background: "#00ADEF",
              border: "none",
              color: "#fff",
              marginBottom: 20,
            }}
          >
            Verify
          </Button>
        </div>,
      ]}
    >
      <div style={{ textAlign: "center" }}>
        <Player
          src="https://lottie.host/1f2eb8bb-26af-4a36-a27b-2d5ce293e7f9/2eQX4bLODe.json"
          className="player"
          loop
          autoplay
          style={{ width: "30%" }}
        />
        <div>
          <p>
            Enter the verification code we sent to <br /> {new_email}
          </p>
        </div>
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 20 }}
        >
          {otpFields.map((otp, index) => (
            <Input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              style={{
                width: 63,
                height: 63,
                textAlign: "center",
                margin: "0 5px",
                borderColor: "#b0b0b0",
              }}
              value={otp}
              onChange={(e) => handleInputChange(index, e.target.value)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default EmailVerificationModal;
